// Tile Data Generator - Maps cell data to KPI tiles
import { basis, domain } from '../state/globalSignals.js'
import { getGlobalStats } from './globalStats.js'

// Cache global stats
let globalStatsCache = null

/**
 * Calculate percentile score for a value
 * @param {number} value - Current cell's value
 * @param {number} min - Global minimum
 * @param {number} max - Global maximum
 * @param {boolean} inverted - Lower is better (for batch variation, aging)
 * @returns {number} Percentile (0-100)
 */
function calculatePercentile(value, min, max, inverted = false) {
  // Fix: Use explicit null/undefined check instead of !value (0 is valid!)
  if (value == null || min == null || max == null || min === max) return null

  // If value is 0 and min is 0, return 0 percentile
  if (value === 0 && min === 0) return 0

  let percentile = ((value - min) / (max - min)) * 100

  // Invert for metrics where lower is better
  if (inverted) {
    percentile = 100 - percentile
  }

  return Math.max(0, Math.min(100, percentile))
}

/**
 * Format unit with k prefix for large values
 * @param {number} value - Value to check
 * @param {string} baseUnit - Base unit (W, A, etc.)
 * @returns {object} { value, unit }
 */
function formatLargeUnit(value, baseUnit) {
  if (Math.abs(value) >= 1000) {
    return {
      value: value / 1000,
      unit: 'k' + baseUnit
    }
  }
  return { value, unit: baseUnit }
}

/**
 * Generates tile data for a given section and cell data
 * @param {string} sectionId - 'scores', 'mechanical', 'chemical', 'operation', 'resistance'
 * @param {object} cellData - Normalized cell data
 * @returns {array} Array of tile objects
 */
export async function getTilesForSection(sectionId, cellData) {
  // Load global stats once
  if (!globalStatsCache) {
    globalStatsCache = await getGlobalStats()
  }

  const currentBasis = basis.value
  const currentDomain = domain.value

  const m_cell_kg = cellData?.mechanical?.mass_g ? cellData.mechanical.mass_g / 1000 : null
  const vol_cell_L = cellData?.mechanical?.vol_cell_cm3 ? cellData.mechanical.vol_cell_cm3 / 1000 : null

  switch (sectionId) {
    case 'scores':
      return getScoresTiles(cellData, currentBasis, currentDomain, m_cell_kg, vol_cell_L, globalStatsCache)

    case 'electrical':
      return getElectricalTiles(cellData, currentBasis, currentDomain, m_cell_kg, vol_cell_L, globalStatsCache)

    case 'thermal':
      return getThermalTiles(cellData, currentBasis, m_cell_kg, vol_cell_L)

    case 'mechanical':
      return getMechanicalTiles(cellData, currentBasis, m_cell_kg, vol_cell_L)

    case 'chemical':
      return getChemicalTiles(cellData)

    case 'operation':
      return getOperationTiles(cellData)

    case 'resistance':
      return getResistanceTiles(cellData, currentBasis, m_cell_kg, vol_cell_L)

    default:
      return []
  }
}

// Scores Tiles
function getScoresTiles(cellData, currentBasis, currentDomain, m_cell_kg, vol_cell_L, globalStats) {
  const tiles = []

  // Energy/Capacity (domain-dependent)
  if (currentDomain === 'energy-power') {
    // Energy at C/10 discharge - use SI units from API (J → Wh for display)
    const W_abs = cellData?.electrical?.W_C10 || 0  // J
    const W_grav = cellData?.electrical?.W_C10_grav || 0  // J/kg
    const W_vol = cellData?.electrical?.W_C10_vol || 0  // J/m³

    // Get value based on basis (convert J to Wh for display)
    let val, valSI
    if (currentBasis === 'grav') {
      valSI = W_grav
      val = W_grav / 3600  // J/kg → Wh/kg
    } else if (currentBasis === 'vol') {
      valSI = W_vol
      val = W_vol / 3600000  // J/m³ → Wh/L (1 m³ = 1000 L)
    } else {
      valSI = W_abs
      val = W_abs / 3600  // J → Wh
    }

    const unit = currentBasis === 'grav' ? 'Wh/kg' : currentBasis === 'vol' ? 'Wh/L' : 'Wh'

    // Calculate percentile using the CORRECT basis from globalStats
    const energyStats = globalStats?.energy?.[currentBasis]
    const percentile = calculatePercentile(
      valSI,  // Use the SI value for the current basis
      energyStats?.min,
      energyStats?.max,
      false
    )

    // Debug logging
    console.log('⚡ Energy Tile Debug:', {
      basis: currentBasis,
      W_abs: `${W_abs} J (${(W_abs/3600).toFixed(1)} Wh)`,
      W_grav: `${W_grav} J/kg (${(W_grav/3600).toFixed(1)} Wh/kg)`,
      W_vol: `${W_vol} J/m³ (${(W_vol/3600000).toFixed(1)} Wh/L)`,
      valSI: `${valSI} (SI unit for ${currentBasis})`,
      displayValue: `${val.toFixed(0)} ${unit}`,
      percentile: percentile,
      energyStats: energyStats
    })

    tiles.push({
      id: 'energy',
      label: 'Energy',
      value: val.toFixed(0),
      unit,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
        </svg>
      ),
      definition: 'The total amount of electrical energy the cell can deliver at C/10 discharge rate.',
      percentile: percentile != null ? percentile.toFixed(0) : null
    })
  } else {
    // Capacity at C/10 discharge - use SI units from API (C → Ah for display)
    const C_abs = cellData?.electrical?.C_C10 || 0  // C (coulomb)
    const C_grav = cellData?.electrical?.C_C10_grav || 0  // C/kg
    const C_vol = cellData?.electrical?.C_C10_vol || 0  // C/m³

    // Get value based on basis (convert C to Ah for display)
    let val, valSI
    if (currentBasis === 'grav') {
      valSI = C_grav
      val = C_grav / 3600  // C/kg → Ah/kg
    } else if (currentBasis === 'vol') {
      valSI = C_vol
      val = C_vol / 3600000  // C/m³ → Ah/L
    } else {
      valSI = C_abs
      val = C_abs / 3600  // C → Ah
    }

    const unit = currentBasis === 'grav' ? 'Ah/kg' : currentBasis === 'vol' ? 'Ah/L' : 'Ah'

    // Calculate percentile using the CORRECT basis from globalStats
    const capacityStats = globalStats?.capacity?.[currentBasis]
    const percentile = calculatePercentile(
      valSI,  // Use the SI value (C) for the current basis
      capacityStats?.min,
      capacityStats?.max,
      false
    )

    tiles.push({
      id: 'capacity',
      label: 'Capacity',
      value: val.toFixed(1),
      unit,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="1" y="6" width="18" height="12" rx="2" ry="2"></rect>
          <line x1="23" y1="13" x2="23" y2="11"></line>
        </svg>
      ),
      definition: 'The total charge the cell can deliver at C/10 discharge rate.',
      percentile: percentile != null ? percentile.toFixed(0) : null
    })
  }

  // Power/Current (domain-dependent)
  if (currentDomain === 'energy-power') {
    // Peak power - use SI units from API
    const P_abs = cellData?.electrical?.P_peak || 0  // W
    const P_grav = cellData?.electrical?.P_peak_grav || 0  // W/kg
    const P_vol = cellData?.electrical?.P_peak_vol || 0  // W/m³

    // Get value based on basis
    let val
    if (currentBasis === 'grav') {
      val = P_grav
    } else if (currentBasis === 'vol') {
      val = P_vol / 1000  // W/m³ → W/L
    } else {
      val = P_abs
    }

    // Store original value before formatting
    const valSI = currentBasis === 'grav' ? P_grav : currentBasis === 'vol' ? P_vol : P_abs

    // Format with k prefix if >= 1000
    const baseUnit = currentBasis === 'grav' ? 'W/kg' : currentBasis === 'vol' ? 'W/L' : 'W'
    const formatted = formatLargeUnit(val, baseUnit)

    // Calculate percentile using the CORRECT basis from globalStats
    const powerStats = globalStats?.power?.[currentBasis]
    const percentile = calculatePercentile(
      valSI,  // Use original SI value (not formatted with /1000)
      powerStats?.min,
      powerStats?.max,
      false
    )

    tiles.push({
      id: 'power',
      label: 'Peak Power',
      value: formatted.value.toFixed(formatted.unit.includes('k') ? 2 : 0),
      unit: formatted.unit,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 3a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3H6a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3V6a3 3 0 0 0-3-3 3 3 0 0 0-3 3 3 3 0 0 0 3 3h12a3 3 0 0 0 3-3 3 3 0 0 0-3-3z"></path>
        </svg>
      ),
      definition: 'The maximum power output the cell can deliver.',
      percentile: percentile != null ? percentile.toFixed(0) : null
    })
  } else {
    // Peak current - use SI units from API
    const I_abs = cellData?.electrical?.I_peak || 0  // A
    const I_grav = cellData?.electrical?.I_peak_grav || 0  // A/kg
    const I_vol = cellData?.electrical?.I_peak_vol || 0  // A/m³

    // Get value based on basis
    let val
    if (currentBasis === 'grav') {
      val = I_grav
    } else if (currentBasis === 'vol') {
      val = I_vol / 1000  // A/m³ → A/L
    } else {
      val = I_abs
    }

    // Store original value before formatting
    const valSI = currentBasis === 'grav' ? I_grav : currentBasis === 'vol' ? I_vol : I_abs

    // Format with k prefix if >= 1000
    const baseUnit = currentBasis === 'grav' ? 'A/kg' : currentBasis === 'vol' ? 'A/L' : 'A'
    const formatted = formatLargeUnit(val, baseUnit)

    // Calculate percentile using power stats (I = P/V, and voltage is relatively constant)
    const powerStats = globalStats?.power?.[currentBasis]
    // Approximate: divide power by ~3.6V average
    const percentile = calculatePercentile(
      valSI,
      powerStats?.min ? powerStats.min / 3.6 : null,
      powerStats?.max ? powerStats.max / 3.6 : null,
      false
    )

    tiles.push({
      id: 'current',
      label: 'Peak Current',
      value: formatted.value.toFixed(formatted.unit.includes('k') ? 2 : 1),
      unit: formatted.unit,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
          <polyline points="17 6 23 6 23 12"></polyline>
        </svg>
      ),
      definition: 'The maximum current the cell can deliver.',
      percentile: percentile != null ? percentile.toFixed(0) : null
    })
  }

  // Batch Variation (lower is better - inverted percentile)
  // Use CAP_spread_std_rel (standard deviation, relative) - this is the preferred metric
  const batchVal = cellData?.batch?.CAP_spread_std_rel || 0
  const batchPercentile = calculatePercentile(
    batchVal,
    globalStats?.minBatchVar,
    globalStats?.maxBatchVar,
    true  // Inverted: lower is better
  )

  tiles.push({
    id: 'batch',
    label: 'Cell-to-Cell Variation',
    value: (batchVal * 100).toFixed(2), // Convert fraction to percent
    unit: '%',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="20" x2="12" y2="10"></line>
        <line x1="18" y1="20" x2="18" y2="4"></line>
        <line x1="6" y1="20" x2="6" y2="16"></line>
      </svg>
    ),
    definition: 'Statistical variation in capacity across production batches (standard deviation). Lower is better.',
    percentile: batchPercentile != null ? batchPercentile.toFixed(0) : null
  })

  // Initial Aging (lower is better - inverted percentile)
  // Use CAP_loss_rel (capacity loss, relative) - this is the aging metric
  // Can be negative (capacity gain = improvement) or positive (capacity loss)
  const agingVal = cellData?.aging?.CAP_loss_rel ?? 0  // Keep sign (negative = better)
  const agingPercentile = calculatePercentile(
    agingVal,  // Use actual value with sign
    globalStats?.minInitialAging,  // Can be negative
    globalStats?.maxInitialAging,
    true  // Inverted: lower is better
  )

  // Format value: negative (gain) shows -, positive (loss) shows +
  const agingPct = agingVal * 100
  const agingDisplay = agingPct < 0
    ? `${agingPct.toFixed(2)}`          // Negative = capacity gain (natural minus sign)
    : agingPct > 0
    ? `+${agingPct.toFixed(2)}`         // Positive = capacity loss (add plus sign)
    : `0.00`                            // Exactly 0

  tiles.push({
    id: 'aging',
    label: 'Post-Formation Loss',
    value: agingDisplay,
    unit: '%',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 6 12 12 16 14"></polyline>
      </svg>
    ),
    definition: 'Capacity change during initial storage and formation cycles. Positive values (+) indicate capacity gain, negative values (-) indicate capacity loss. Higher is better.',
    percentile: agingPercentile != null ? agingPercentile.toFixed(0) : null
  })

  return tiles
}

// Electrical Tiles - Voltage, Energy, Power, Capacity, Impedance, Current
function getElectricalTiles(cellData, currentBasis, currentDomain, m_cell_kg, vol_cell_L, globalStats) {
  const tiles = []
  const elec = cellData?.electrical || {}

  // Voltage
  tiles.push({
    id: 'voltage',
    label: 'Voltage Range',
    value: `${elec.V_min?.toFixed(2) || '–'} - ${elec.V_max?.toFixed(2) || '–'}`,
    unit: 'V',
    icon: '⚡',
    definition: 'Minimum and maximum cell voltage'
  })

  // Energy
  const E_c10_Wh = elec.E_c10_Wh || 0
  tiles.push({
    id: 'energy',
    label: 'Energy',
    value: E_c10_Wh.toFixed(1),
    unit: 'Wh',
    icon: '🔋',
    definition: 'Total energy at C/10 discharge rate'
  })

  // Power
  const P_peak_W = elec.P_peak_W || 0
  tiles.push({
    id: 'power',
    label: 'Peak Power',
    value: P_peak_W.toFixed(0),
    unit: 'W',
    icon: '⚡',
    definition: 'Maximum power output'
  })

  // Capacity
  const C_c10_Ah = elec.C_c10_Ah || 0
  tiles.push({
    id: 'capacity',
    label: 'Capacity',
    value: C_c10_Ah.toFixed(2),
    unit: 'Ah',
    icon: '📊',
    definition: 'Capacity at C/10 discharge rate'
  })

  // Impedance DC
  const R_dc_mOhm = elec.R_dc_mOhm || 0
  tiles.push({
    id: 'impedance_dc',
    label: 'DC Impedance',
    value: R_dc_mOhm.toFixed(2),
    unit: 'mΩ',
    icon: '⚡',
    definition: 'DC internal resistance'
  })

  // Impedance AC
  const R_ac_mOhm = elec.R_ac_mOhm || 0
  tiles.push({
    id: 'impedance_ac',
    label: 'AC Impedance',
    value: R_ac_mOhm.toFixed(2),
    unit: 'mΩ',
    icon: '〜',
    definition: 'AC internal resistance'
  })

  // Current Continuous
  const I_cont_A = elec.I_cont_A || 0
  tiles.push({
    id: 'current_cont',
    label: 'Continuous Current',
    value: I_cont_A.toFixed(1),
    unit: 'A',
    icon: '→',
    definition: 'Continuous current capability'
  })

  // Current Peak
  const I_peak_A = elec.I_peak_A || 0
  tiles.push({
    id: 'current_peak',
    label: 'Peak Current',
    value: I_peak_A.toFixed(1),
    unit: 'A',
    icon: '⚡',
    definition: 'Peak current capability'
  })

  return tiles
}

// Thermal Tiles - Temperature, Heat, Efficiency
function getThermalTiles(cellData, currentBasis, m_cell_kg, vol_cell_L) {
  const tiles = []
  const thermal = cellData?.thermal || {}
  const elec = cellData?.electrical || {}

  // Thermal Energy Discharge
  const W_loss_dc_25 = thermal.W_loss_dc_25 || 0
  tiles.push({
    id: 'thermal_energy_dc',
    label: 'Thermal Energy (Discharge)',
    value: W_loss_dc_25.toFixed(2),
    unit: 'Wh',
    icon: '🔥',
    definition: 'Heat generated during discharge at 1C and 25°C'
  })

  // Thermal Energy Charge
  const W_loss_ch_25 = thermal.W_loss_ch_25 || 0
  tiles.push({
    id: 'thermal_energy_ch',
    label: 'Thermal Energy (Charge)',
    value: W_loss_ch_25.toFixed(2),
    unit: 'Wh',
    icon: '🔥',
    definition: 'Heat generated during charge at 1C and 25°C'
  })

  // Thermal Power Discharge
  const P_loss_dc_25 = thermal.P_loss_dc_25 || 0
  tiles.push({
    id: 'thermal_power_dc',
    label: 'Thermal Power (Discharge)',
    value: P_loss_dc_25.toFixed(2),
    unit: 'W',
    icon: '🔥',
    definition: 'Average heat dissipation during discharge at 1C and 25°C'
  })

  // Thermal Power Charge
  const P_loss_ch_25 = thermal.P_loss_ch_25 || 0
  tiles.push({
    id: 'thermal_power_ch',
    label: 'Thermal Power (Charge)',
    value: P_loss_ch_25.toFixed(2),
    unit: 'W',
    icon: '🔥',
    definition: 'Average heat dissipation during charge at 1C and 25°C'
  })

  // Temperature Rise
  const delta_T_dc_25 = thermal.delta_T_dc_25 || 0
  tiles.push({
    id: 'temperature_rise',
    label: 'Temperature Rise',
    value: delta_T_dc_25.toFixed(1),
    unit: 'K',
    icon: '🌡️',
    definition: 'Temperature increase during discharge'
  })

  // Efficiency Discharge
  const eta_dc_pct = elec.eta_dc_pct || 0
  tiles.push({
    id: 'efficiency_dc',
    label: 'Efficiency (Discharge)',
    value: eta_dc_pct.toFixed(1),
    unit: '%',
    icon: '✓',
    definition: 'Energy efficiency during discharge at 1C and 25°C'
  })

  // Efficiency Charge
  const eta_ch_pct = elec.eta_ch_pct || 0
  tiles.push({
    id: 'efficiency_ch',
    label: 'Efficiency (Charge)',
    value: eta_ch_pct.toFixed(1),
    unit: '%',
    icon: '✓',
    definition: 'Energy efficiency during charge at 1C and 25°C'
  })

  return tiles
}

// Mechanical Tiles
function getMechanicalTiles(cellData, currentBasis, m_cell_kg, vol_cell_L) {
  const tiles = []

  // Mass
  const mass_g = cellData?.mechanical?.mass_g || 0
  tiles.push({
    id: 'mass',
    label: 'Mass',
    value: mass_g.toFixed(1),
    unit: 'g',
    icon: '⚖️',
    definition: 'Total mass of the cell including casing and terminals.',
    rank: 45,
    percentile: 60
  })

  // Volume
  const vol_cm3 = cellData?.mechanical?.vol_cell_cm3 || 0
  tiles.push({
    id: 'volume',
    label: 'Volume',
    value: vol_cm3.toFixed(1),
    unit: 'cm³',
    icon: '📦',
    definition: 'Total volume of the cell including casing.',
    rank: 52,
    percentile: 55
  })

  // Density
  if (mass_g && vol_cm3) {
    const density = mass_g / vol_cm3
    tiles.push({
      id: 'density',
      label: 'Density',
      value: density.toFixed(2),
      unit: 'g/cm³',
      icon: '🔬',
      definition: 'Mass per unit volume, indicating material density.',
      rank: 30,
      percentile: 70
    })
  }

  // Dimensions
  const height_mm = cellData?.mechanical?.height_mm || 0
  const width_mm = cellData?.mechanical?.width_mm || 0
  const length_mm = cellData?.mechanical?.length_mm || 0

  if (height_mm) {
    tiles.push({
      id: 'dimensions',
      label: 'Dimensions',
      value: `${length_mm} × ${width_mm} × ${height_mm}`,
      unit: 'mm',
      icon: '📏',
      definition: 'Physical dimensions: Length × Width × Height.',
      rank: null,
      percentile: null
    })
  }

  return tiles
}

// Chemical Tiles
function getChemicalTiles(cellData) {
  const tiles = []

  // Cathode Material
  if (cellData?.chemical?.cathode) {
    tiles.push({
      id: 'cathode',
      label: 'Cathode',
      value: cellData.chemical.cathode,
      unit: '',
      icon: '🧪',
      definition: 'Cathode material chemistry.',
      rank: null,
      percentile: null
    })
  }

  // Anode Material
  if (cellData?.chemical?.anode) {
    tiles.push({
      id: 'anode',
      label: 'Anode',
      value: cellData.chemical.anode,
      unit: '',
      icon: '⚗️',
      definition: 'Anode material chemistry.',
      rank: null,
      percentile: null
    })
  }

  // Electrolyte
  if (cellData?.chemical?.electrolyte) {
    tiles.push({
      id: 'electrolyte',
      label: 'Electrolyte',
      value: cellData.chemical.electrolyte,
      unit: '',
      icon: '💧',
      definition: 'Electrolyte composition.',
      rank: null,
      percentile: null
    })
  }

  return tiles
}

// Operation Tiles
function getOperationTiles(cellData) {
  const tiles = []

  // Operating Voltage Range
  const v_min = cellData?.electrical?.V_min || 0
  const v_max = cellData?.electrical?.V_max || 0

  if (v_min && v_max) {
    tiles.push({
      id: 'voltage',
      label: 'Voltage Range',
      value: `${v_min.toFixed(2)} – ${v_max.toFixed(2)}`,
      unit: 'V',
      icon: '⚡',
      definition: 'Operating voltage range from minimum to maximum.',
      rank: null,
      percentile: null
    })
  }

  // Temperature Range
  const t_min = cellData?.thermal?.T_min_C || 0
  const t_max = cellData?.thermal?.T_max_C || 0

  if (t_min && t_max) {
    tiles.push({
      id: 'temperature',
      label: 'Temperature Range',
      value: `${t_min} – ${t_max}`,
      unit: '°C',
      icon: '🌡️',
      definition: 'Operating temperature range.',
      rank: null,
      percentile: null
    })
  }

  // Cycle Life
  const cycles = cellData?.aging?.cycle_life || 0
  if (cycles) {
    tiles.push({
      id: 'cycles',
      label: 'Cycle Life',
      value: cycles.toFixed(0),
      unit: 'cycles',
      icon: '🔄',
      definition: 'Expected number of charge-discharge cycles.',
      rank: 35,
      percentile: 75
    })
  }

  return tiles
}

// Resistance Tiles
function getResistanceTiles(cellData, currentBasis, m_cell_kg, vol_cell_L) {
  const tiles = []

  // DCIR
  const R_dc = cellData?.electrical?.R_dc_mOhm || 0
  const R_dc_val = currentBasis === 'grav' && m_cell_kg
    ? R_dc * m_cell_kg
    : currentBasis === 'vol' && vol_cell_L
    ? R_dc * vol_cell_L
    : R_dc
  const R_dc_unit = currentBasis === 'grav' ? 'mΩ·kg' : currentBasis === 'vol' ? 'mΩ·L' : 'mΩ'

  tiles.push({
    id: 'r_dc',
    label: 'DCIR',
    value: R_dc_val.toFixed(2),
    unit: R_dc_unit,
    icon: '⚡',
    definition: 'Direct current internal resistance.',
    rank: 40,
    percentile: 68
  })

  // AC Resistance (if available)
  const R_ac = cellData?.electrical?.R_ac_mOhm || 0
  if (R_ac) {
    const R_ac_val = currentBasis === 'grav' && m_cell_kg
      ? R_ac * m_cell_kg
      : currentBasis === 'vol' && vol_cell_L
      ? R_ac * vol_cell_L
      : R_ac
    const R_ac_unit = currentBasis === 'grav' ? 'mΩ·kg' : currentBasis === 'vol' ? 'mΩ·L' : 'mΩ'

    tiles.push({
      id: 'r_ac',
      label: 'AC Resistance',
      value: R_ac_val.toFixed(2),
      unit: R_ac_unit,
      icon: '〜',
      definition: 'Alternating current internal resistance.',
      rank: 38,
      percentile: 70
    })
  }

  return tiles
}
